export type LocaleSource = 'query' | 'cookie' | 'default';

export interface ResolvedLocale {
  formatLocale: string;
  uiLocale: string;
  source: LocaleSource;
}

const LOCALE_COOKIE = 'selfservice_locale';
const DEFAULT_FORMAT_LOCALE = 'da-DK';
const DEFAULT_UI_LOCALE = 'da';

const QUERY_KEYS = ['locale', 'lang', 'culture'];

const normalizeLocale = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return DEFAULT_FORMAT_LOCALE;
  }

  const lower = trimmed.toLowerCase();
  switch (lower) {
    case 'da':
      return 'da-DK';
    case 'en':
      return 'en-US';
    case 'sv':
      return 'sv-SE';
    case 'no':
    case 'nb':
      return 'nb-NO';
    case 'fi':
      return 'fi-FI';
    default:
      return trimmed;
  }
};

const toUiLocale = (formatLocale: string): string => {
  const normalized = formatLocale.trim();
  if (!normalized) {
    return DEFAULT_UI_LOCALE;
  }

  const lower = normalized.toLowerCase();
  if (lower.startsWith('nb')) {
    return 'no';
  }

  const dashIndex = lower.indexOf('-');
  return dashIndex > 0 ? lower.slice(0, dashIndex) : lower;
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.split('=');
    if (key === name) {
      return decodeURIComponent(rest.join('='));
    }
  }

  return null;
};

const writeCookie = (name: string, value: string): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);

  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

const readQueryLocale = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  for (const key of QUERY_KEYS) {
    const value = params.get(key);
    if (value) {
      return value;
    }
  }

  return null;
};

export const resolveLocale = (): ResolvedLocale => {
  const queryLocale = readQueryLocale();
  if (queryLocale) {
    const formatLocale = normalizeLocale(queryLocale);
    const uiLocale = toUiLocale(formatLocale);
    writeCookie(LOCALE_COOKIE, formatLocale);
    return { formatLocale, uiLocale, source: 'query' };
  }

  const cookieLocale = readCookie(LOCALE_COOKIE);
  if (cookieLocale) {
    const formatLocale = normalizeLocale(cookieLocale);
    return { formatLocale, uiLocale: toUiLocale(formatLocale), source: 'cookie' };
  }

  return {
    formatLocale: DEFAULT_FORMAT_LOCALE,
    uiLocale: DEFAULT_UI_LOCALE,
    source: 'default'
  };
};

export const getFormatLocale = (): string => resolveLocale().formatLocale;

export const getUiLocale = (): string => resolveLocale().uiLocale;

export const applyDocumentLocale = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.lang = getUiLocale();
};

export const formatCurrency = (amount: number, currency: string = 'DKK'): string => {
  const formatted = new Intl.NumberFormat(getFormatLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  return `${formatted} ${currency}`;
};
